import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const GrandIllusion = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });

  const imageFilter = useTransform(
    scrollYProgress,
    [0, 0.4, 1],
    ['grayscale(100%) brightness(0.4)', 'grayscale(50%) brightness(0.65)', 'grayscale(0%) brightness(0.9)']
  );

  const warmth = useTransform(scrollYProgress, [0, 1], [0, 0.15]);

  return (
    <section ref={ref} className="relative bg-dark border-t border-white/5 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[90vh]">
        {/* Parallax Image */}
        <div className="relative lg:sticky lg:top-0 lg:h-screen overflow-hidden order-2 lg:order-1">
          <motion.img
            src="https://media-manager-c.questera.ai/greta-media/8c84f85263efda659889de32f1f32e795be69e9a2504f254db44deecf03d5d2eaf8d8138b6c4ad97d7d004f4d5618742/images/aW1hZ2UvcG5n/276ace7e98fc392a3a18ddcb52406911.png"
            alt="Beziehungsheilung"
            style={{ filter: imageFilter }}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.15), transparent 60%)',
              opacity: warmth,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark lg:block hidden" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent" />
        </div>

        {/* Text */}
        <div className="relative z-10 flex flex-col justify-center px-8 md:px-14 py-28 space-y-10 order-1 lg:order-2">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold tracking-[0.3em] uppercase text-xs font-semibold"
          >
            Die Große Illusion
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-4xl md:text-5xl font-serif text-cream leading-tight"
          >
            Es liegt nicht an dir.
            <span className="italic text-wine-light block mt-2">
              Es liegt an deiner Programmierung.
            </span>
          </motion.h2>

          {[
            {
              delay: 0.2,
              text: 'Du hast dir tausendmal gesagt: "Wenn ich nur geduldiger wäre. Wenn ich nur besser kommunizieren würde. Wenn ich nur weniger brauchen würde." Du hast dich analysiert, optimiert, verändert. Du bist in Therapie gegangen, hast Bücher gelesen, hast Grenzen gesetzt – und sie drei Tage später wieder aufgegeben, weil die Stille, die danach kam, unerträglich war. Das liegt nicht daran, dass du schwach bist. Das liegt daran, dass dein inneres Sicherheitssystem auf Drama kalibriert wurde.',
            },
            {
              delay: 0.4,
              highlight: true,
              text: 'Solange Frieden sich langweilig anfühlt, wirst du Chaos anziehen. Das ist keine Schwäche. Das ist Neurobiologie.',
            },
            {
              delay: 0.6,
              text: 'Irgendwo in deiner frühen Geschichte – vielleicht war es ein Vater, der emotional unverfügbar war, aber an den du dein ganzes kleines Herz verschenkt hast. Vielleicht war es eine Mutter, deren Liebe sich immer ein bisschen bedingt anfühlte. Vielleicht war es eine erste große Liebe, die dich gelehrt hat, dass Verlassenwerden das Schlimmste ist, was passieren kann. Dein Gehirn hat eine Gleichung aufgestellt: Intensive Emotionen gleich Liebe. Unsicherheit gleich Anziehung. Und diese Gleichung läuft seitdem still im Hintergrund, wählt deine Partner aus, bevor dein bewusstes Denken auch nur ein Wort sagen kann.',
            },
            {
              delay: 0.8,
              gold: true,
              text: 'Du bist nicht kaputt. Du bist nicht verflucht. Du bist eine brillante, tiefgefühlte Frau, deren inneres Sicherheitssystem ein Update braucht. Das ist alles.',
            },
          ].map((block, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: block.delay, duration: 0.8 }}
              className={`font-light leading-relaxed text-lg ${
                block.gold
                  ? 'text-gold font-serif italic text-xl'
                  : block.highlight
                  ? 'text-cream font-serif italic text-xl'
                  : 'text-cream/65'
              }`}
            >
              {block.text}
            </motion.p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GrandIllusion;